import { makeAutoObservable, observable, runInAction, action } from "mobx";
import { toast } from "../components/ui/toast";
import channelsService from "../services/channels.services";
import type { channelIem } from "../types/ChannelsResponse";
import type {
  ChannelMedia,
  channelSchemaItem,
} from "../types/ChannelsResponse";

export interface channelMetaType {
  name: string | { en: string; ar: string };
  type: string;
  description?: string | { en: string; ar: string };
  logo?: string;
  eligibleForCampaigns?: boolean;
  onboardingInstructions?: {
    en?: {
      title: string;
      steps: string[];
      tips: string[];
      documentation?: string;
    };
    ar?: {
      title: string;
      steps: string[];
      tips: string[];
      documentation?: string;
    };
  };
}

interface FieldData {
  name: string[];
  value: any;
}

export default class channelsStore {
  channels: ChannelMedia[] = [];
  channelSchema: channelSchemaItem[] = [];
  isChannelLoading = true;
  isChannelSchemaLoading = false;
  isChannelAddLoading = false;
  isFetchChannelsLoading = false;
  isUpdateChannelLoading = false;
  isDeleteChannelLoading = false;
  channelName = "";
  channelMetaData: channelMetaType = {
    name: "",
    type: "",
  };

  /// all channels states
  pageNum = 1;
  search = "";
  limit = 10;
  channelType = "";

  allChannels: channelIem[] = [];
  totalPages = 0;
  totalCount = 0;
  currentPage = 1;

  ///update channel
  updatedChannelObj = {} as channelIem;
  isUpdateChannel = false;
  changedFieldsArr: FieldData[] = [];
  channelId = "";

  /// table search column
  searchedColumn = "";

  constructor() {
    makeAutoObservable(this, {
      channels: observable,
      updatedChannelObj: observable,
      pageNum: observable,
      search: observable,
      limit: observable,
      channelType: observable,
      allChannels: observable,
      totalPages: observable,
      totalCount: observable,
      currentPage: observable,
      setPageNum: action,
      setLimit: action,
      setSearch: action,
      setChannelType: action,
      fetchAllChannels: action,
    });
  }

  private channelsMetaDataService = new channelsService();

  fetchChannelsMetadata = async () => {
    try {
      runInAction(() => {
        this.isChannelLoading = true;
      });

      // Fetch data from service
      const { data } = await this.channelsMetaDataService.getChannelsMetadata();

      runInAction(() => {
        this.isChannelLoading = false;
        console.log("channelsMeta", data);
        const finalResponse = data.filter(ele => ele);
        this.channels = finalResponse;
      });
    } catch (error: any) {
      runInAction(() => {
        toast.error(error.message || "Error fetching Channels Metadata");
      });
    } finally {
      this.isChannelLoading = false;
    }
  };

  // fetch channel schema
  // channels.store.ts - Update fetchChannelSchema method
  fetchChannelSchema = async () => {
    try {
      runInAction(() => {
        this.isChannelSchemaLoading = true;
      });
      const { data } = await this.channelsMetaDataService.getChannelSchema(
        this.channelName,
      );
      const { properties, channelMetaData } = data;

      // Get current language from i18n or localStorage
      const currentLang = localStorage.getItem("i18nextLng") || "en";

      runInAction(() => {
        // Store multilingual metadata
        this.channelMetaData = channelMetaData;

        // Process properties with current language
        this.channelSchema = properties.map((prop: any) => ({
          ...prop,
          // Extract label for current language
          label:
            typeof prop.label === "object"
              ? prop.label[currentLang] || prop.label.en || prop.name
              : prop.label,
        }));

        this.isChannelSchemaLoading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        toast.error(error.message || "Error fetching data");
      });
    } finally {
      this.isChannelSchemaLoading = false;
    }
  };

  //// add channel
  addChannelFunc = async (channelData: any) => {
    try {
      runInAction(() => {
        this.isChannelAddLoading = true;
      });
      const response = await this.channelsMetaDataService.addChannel({
        ...channelData,
        channel_type: this.channelMetaData.type,
      });

      runInAction(() => {
        this.isChannelAddLoading = false;
        console.log("channelAdded", response);
        toast.success(response.message || "Channel created successfully");
      });
      return response;
    } catch (error: any) {
      runInAction(() => {
        toast.error(error.message || "Error Adding Channel");
        this.isChannelAddLoading = false;
      });
      throw error;
    } finally {
      this.isChannelAddLoading = false;
    }
  };

  /// get all channels - UPDATED WITH BETTER LOGGING
  fetchAllChannels = async () => {
    try {
      console.log("🚀 [Store] fetchAllChannels called with:", {
        limit: this.limit,
        pageNum: this.pageNum,
        search: this.search,
        channelType: this.channelType,
      });

      runInAction(() => {
        this.isFetchChannelsLoading = true;
      });

      const { data, totalCount, totalPages, currentPage } =
        await this.channelsMetaDataService.getAllChannels(
          this.limit,
          this.pageNum,
          this.search,
          this.channelType,
        );

      console.log("✅ [Store] fetchAllChannels response:", {
        dataCount: data.length,
        totalCount,
        totalPages,
        currentPage,
        firstChannelType: data[0]?.channel_type,
      });

      runInAction(() => {
        console.log("allChannels before update:", this.allChannels.length);
        this.allChannels = [...data];
        this.totalCount = totalCount;
        this.totalPages = totalPages;
        this.currentPage = currentPage;
        this.isFetchChannelsLoading = false;

        console.log("allChannels after update:", this.allChannels.length);
      });
    } catch (error: any) {
      console.error("❌ [Store] fetchAllChannels error:", error);
      runInAction(() => {
        toast.error(error.message || "Error fetching data");
        this.isFetchChannelsLoading = false;
      });
    }
  };

  fetchCampaignChannels = async () => {
    try {
      console.log("🚀 [Store] fetchCampaignChannels called - fetching only WhatsApp & Email");

      runInAction(() => {
        this.isFetchChannelsLoading = true;
      });

      // Use high limit to get all available WhatsApp and Email channels
      const { data, totalCount, totalPages, currentPage } =
        await this.channelsMetaDataService.getAllChannels(
          100, // High limit to get all channels
          1,   // First page
          "",  // No search query
          "",  // No single channel type
          ["WHATSAPP", "EMAIL"] // Filter for WhatsApp and Email only
        );

      console.log("✅ [Store] fetchCampaignChannels response:", {
        dataCount: data.length,
        totalCount,
        channels: data.map(c => ({ id: c.id, name: c.name, type: c.channel_type }))
      });

      runInAction(() => {
        this.allChannels = [...data];
        this.totalCount = totalCount;
        this.totalPages = totalPages;
        this.currentPage = currentPage;
        this.isFetchChannelsLoading = false;
      });

      return data;
    } catch (error: any) {
      console.error("❌ [Store] fetchCampaignChannels error:", error);
      runInAction(() => {
        toast.error(error.message || "Error fetching campaign channels");
        this.isFetchChannelsLoading = false;
      });
      return [];
    }
  };

  /// get channel by id
  fetchChannel = async (id: string) => {
    try {
      const data = await this.channelsMetaDataService.getChannel(id);
      return data;
    } catch (error: any) {
      runInAction(() => {
        toast.error(error.message || "Error fetching data");
      });
      return null;
    }
  };

  updateChannelFunc = async (channelData: any) => {
    try {
      runInAction(() => {
        this.isUpdateChannelLoading = true;
      });
      const updatedRecord = await this.channelsMetaDataService.updateChannel(
        this.channelId,
        { ...channelData, channel_type: this.updatedChannelObj.channel_type },
      );

      runInAction(() => {
        this.allChannels = this.allChannels.map(channel =>
          channel.id === this.channelId ? updatedRecord : channel,
        );
        this.fetchAllChannels();
        console.log("updatedRecord", updatedRecord);
        this.isUpdateChannelLoading = false;
        toast.success(
          updatedRecord.message || "operation completed successfully",
        );
      });
    } catch (error: any) {
      runInAction(() => {
        toast.error(error.message || "Error updating Channel");
        this.isUpdateChannelLoading = false;
      });
    } finally {
      this.isUpdateChannelLoading = false;
    }
  };

  //// delete channel
  deleteChannelFunc = async () => {
    try {
      runInAction(() => {
        this.isDeleteChannelLoading = true;
      });
      const response = await this.channelsMetaDataService.deleteChannel(
        this.channelId,
      );

      runInAction(() => {
        this.allChannels = this.allChannels.filter(
          channel => channel.id !== this.channelId,
        );
        this.isDeleteChannelLoading = false;
        toast.success(response.message);
      });
    } catch (error: any) {
      runInAction(() => {
        toast.error(error.message || "Error Deleting Channel");
        this.isDeleteChannelLoading = false;
      });
    } finally {
      this.isDeleteChannelLoading = false;
    }
  };

  // ACTION methods for MobX
  setChannelName = (channelName: string) => {
    this.channelName = channelName;
  };

  setPageNum = (pageNum: number) => {
    console.log("📄 [Store] setPageNum:", pageNum);
    this.pageNum = pageNum;
  };

  setLimit = (limit: number) => {
    console.log("📏 [Store] setLimit:", limit);
    this.limit = limit;
  };

  setChannelSchema = (channelSchema: []) => {
    this.channelSchema = channelSchema;
  };

  setUpdatedChannelObj = (obj: channelIem) => {
    this.updatedChannelObj = obj;
  };

  setIsUpdateChannel = (data: boolean) => {
    this.isUpdateChannel = data;
  };

  setChangedFieldsArr = (data: FieldData[]) => {
    this.changedFieldsArr = data;
  };

  setChannelId = (id: string) => {
    this.channelId = id;
  };

  setSearch = (data: string) => {
    console.log("🔎 [Store] setSearch:", data);
    this.search = data;
  };

  setChannelType = (data: string) => {
    console.log("🎯 [Store] setChannelType:", {
      from: this.channelType,
      to: data,
    });
    this.channelType = data;
  };

  setSearchedColumn = (dataIndex: string) => {
    this.searchedColumn = dataIndex;
  };
}
